// src/pages/admin/products/BranchProducts.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { 
  Package, 
  Plus, 
  Search, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Filter,
  X
} from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import ProductInventoryService from '../../../services/productInventoryService'
import BranchService from '../../../services/branchService'
import ProductInventoryModel from '../../../utils/productInventoryModel'
import AddProductInventoryModal from '../../../components/modals/AddProductInventoryModal'
import EditProductInventoryModal from '../../../components/modals/EditProductInventoryModal'
import ViewProductModal from '../../../components/modals/ViewProductInventoryModal'
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal'
import PageLoader from '../../../components/ui/PageLoader'
import { usePermissions } from '../../../hooks/usePermissions'
import AuthService from '../../../services/authService'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 
    type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : <Package className="h-5 w-5" />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const BranchProducts = () => {
  // Get current user and permissions
  const currentUser = AuthService.getCurrentUser()
  const permissions = usePermissions(currentUser)
  
  // Extract stable values from permissions to avoid infinite loops
  const isBranchRestricted = permissions.isBranchRestricted
  
  // State management
  const [inventories, setInventories] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState('all')
  const [sortBy, setSortBy] = useState('product')
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedInventory, setSelectedInventory] = useState(null)
  
  // Toast notification
  const [toast, setToast] = useState(null)

  // Helper function for safe property access
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    // Don't reset branch filter for restricted users (managers)
    if (!isBranchRestricted) {
      setBranchFilter('all')
    }
    setSortBy('product')
  }

  // Initial load - use useEffect with empty dependency array to prevent infinite loops
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Log manager access
        if (permissions.logBranchAccess) {
          permissions.logBranchAccess('product_inventory', 'view')
        }
        
        // Check if user is branch-restricted
        const userBranchId = currentUser?.branchId
        
        // Apply branch filtering for restricted users (managers)
        let inventoriesParams = { page: 1, limit: 100 }
        if (isBranchRestricted && userBranchId) {
          inventoriesParams.branchId = userBranchId
          console.log(`🏢 [MANAGER FILTER] Loading product inventory for branch: ${userBranchId}`)
        }
        
        // Fetch inventories and branches in parallel
        const [inventoriesResponse, branchesResponse] = await Promise.all([
          ProductInventoryService.getAllInventories(inventoriesParams),
          BranchService.getAllBranches()
        ])
        
        // Process inventories
        if (inventoriesResponse?.success) {
          const inventoriesData = safeGet(inventoriesResponse, 'data.productInventories', [])
          let validInventories = Array.isArray(inventoriesData) 
            ? inventoriesData.filter(inv => ProductInventoryModel.isValidForDisplay(inv))
            : []
          
          // Additional client-side filtering for branch-restricted users
          if (isBranchRestricted && userBranchId) {
            validInventories = validInventories.filter(inventory => 
              inventory.branchId === userBranchId || 
              (inventory.branch && inventory.branch._id === userBranchId)
            )
            console.log(`🔒 [BRANCH RESTRICTED] Showing ${validInventories.length} inventory items for branch ${userBranchId}`)
          }
          
          console.log('📦 Product inventories loaded:', validInventories.length)
          setInventories(validInventories)
        }
        
        // Process branches - for managers, only show their branch
        if (branchesResponse?.success) {
          const branchesData = safeGet(branchesResponse, 'data.branches', [])
          let filteredBranches = Array.isArray(branchesData) ? branchesData : []
          
          // Filter branches for restricted users
          if (isBranchRestricted && userBranchId) {
            filteredBranches = branchesData.filter(branch => branch._id === userBranchId)
            console.log(`🏢 [BRANCH RESTRICTED] Manager can only see their branch: ${filteredBranches[0]?.name || userBranchId}`)
          }
          
          setBranches(filteredBranches)
        }
        
      } catch (error) {
        console.error('❌ Error fetching data:', error)
        setError(error.message || 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array to prevent infinite loops

  // Separate function for manual refresh
  const refreshData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Check if user is branch-restricted
      const userBranchId = currentUser?.branchId
      
      // Apply branch filtering for restricted users (managers)
      let inventoriesParams = { page: 1, limit: 100 }
      if (isBranchRestricted && userBranchId) {
        inventoriesParams.branchId = userBranchId
        console.log(`🏢 [MANAGER FILTER] Refreshing product inventory for branch: ${userBranchId}`)
      }
      
      // Fetch inventories and branches in parallel
      const [inventoriesResponse, branchesResponse] = await Promise.all([
        ProductInventoryService.getAllInventories(inventoriesParams),
        BranchService.getAllBranches()
      ])
      
      // Process inventories
      if (inventoriesResponse?.success) {
        const inventoriesData = safeGet(inventoriesResponse, 'data.productInventories', [])
        let validInventories = Array.isArray(inventoriesData) 
          ? inventoriesData.filter(inv => ProductInventoryModel.isValidForDisplay(inv))
          : []
        
        // Additional client-side filtering for branch-restricted users
        if (isBranchRestricted && userBranchId) {
          validInventories = validInventories.filter(inventory => 
            inventory.branchId === userBranchId || 
            (inventory.branch && inventory.branch._id === userBranchId)
          )
          console.log(`🔒 [BRANCH RESTRICTED] Showing ${validInventories.length} inventory items for branch ${userBranchId}`)
        }
        
        console.log('📦 Product inventories refreshed:', validInventories.length)
        setInventories(validInventories)
      }
      
      // Process branches - for managers, only show their branch
      if (branchesResponse?.success) {
        const branchesData = safeGet(branchesResponse, 'data.branches', [])
        let filteredBranches = Array.isArray(branchesData) ? branchesData : []
        
        // Filter branches for restricted users
        if (isBranchRestricted && userBranchId) {
          filteredBranches = branchesData.filter(branch => branch._id === userBranchId)
          console.log(`🏢 [BRANCH RESTRICTED] Manager can only see their branch: ${filteredBranches[0]?.name || userBranchId}`)
        }
        
        setBranches(filteredBranches)
      }
      
    } catch (error) {
      console.error('❌ Error refreshing data:', error)
      setError(error.message || 'Failed to refresh data')
    } finally {
      setLoading(false)
    }
  }, [isBranchRestricted, currentUser?.branchId])

  // Auto-set branch filter for managers
  useEffect(() => {
    if (isBranchRestricted && currentUser?.branchId) {
      setBranchFilter(currentUser.branchId)
      console.log(`🎯 [AUTO FILTER] Setting branch filter to manager's branch: ${currentUser.branchId}`)
    }
  }, [isBranchRestricted, currentUser?.branchId])

  // Filter and search inventories
  const getFilteredInventories = () => {
    let filtered = [...inventories]

    // Search filter
    filtered = ProductInventoryService.searchInventories(filtered, searchTerm)
    
    // Branch filter
    if (branchFilter !== 'all') {
      filtered = ProductInventoryService.filterByBranch(filtered, branchFilter)
    }

    // Sort
    filtered = ProductInventoryService.sortInventories(filtered, sortBy === 'product' ? 'product.name' : sortBy, 'asc')

    return filtered
  }

  // Handle inventory created
  const handleInventoryCreated = (newInventories) => {
    if (Array.isArray(newInventories)) {
      setInventories(prev => [...prev, ...newInventories])
      showToast(`${newInventories.length} product(s) added to inventory successfully`)
    } else {
      setInventories(prev => [...prev, newInventories])
      showToast('Product added to inventory successfully')
    }
  }

  // Handle inventory updated
  const handleInventoryUpdated = (updatedInventory) => {
    console.log('📥 Received updated inventory:', updatedInventory)
    
    setInventories(prev => {
      const updated = prev.map(inv => {
        if (inv._id === updatedInventory._id) {
          console.log('🔄 Updating inventory:', {
            old: inv,
            new: updatedInventory
          })
          return updatedInventory
        }
        return inv
      })
      console.log('📦 Updated inventories list:', updated)
      return updated
    })
    
    showToast('Inventory updated successfully')
  }

  // Handle inventory deleted
  const handleInventoryDeleted = async () => {
    if (!selectedInventory) return
    
    try {
      await ProductInventoryService.deleteInventory(selectedInventory._id)
      setInventories(prev => prev.filter(inv => inv._id !== selectedInventory._id))
      showToast('Product removed from inventory successfully')
      setSelectedInventory(null)
      setIsDeleteModalOpen(false)
    } catch (error) {
      console.error('❌ Error deleting inventory:', error)
      showToast(error.message || 'Failed to delete inventory', 'error')
    }
  }

  // Get inventory statistics
  const getInventoryStats = () => {
    const filtered = getFilteredInventories()
    return ProductInventoryService.getInventoryStats(filtered)
  }

  // Loading state
  if (loading && inventories.length === 0) {
    return (
      <PageLoader 
        headerTitle="Branch Products"
        title="Loading inventories..."
      />
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Products Inventory</h1>
            </div>
            <div className="flex space-x-3">
              <Button 
                onClick={refreshData}
                leftIcon={RefreshCw}
                variant="outline"
                className="bg-white border-gray-300"
              >
                Refresh
              </Button>
              <Button 
                onClick={() => setIsAddModalOpen(true)}
                leftIcon={Plus}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Add to Inventory
              </Button>
            </div>
          </div>
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={refreshData} leftIcon={RefreshCw}>
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const filteredData = getFilteredInventories()
  const stats = getInventoryStats()

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Products Inventory</h1>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={refreshData}
              disabled={loading}
              leftIcon={RefreshCw}
              variant="outline"
              className="bg-white border-gray-300"
            >
              Refresh
            </Button>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              leftIcon={Plus}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Add to Inventory
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Package className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Items</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Normal Stock</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.normal}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Low/Out Stock</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.lowStock + stats.outOfStock}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <DollarSign className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Value</p>
                  <p className="text-2xl font-bold text-gray-900">Rs. {stats.totalValue.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Filter Inventory</h2>
              </div>
              {(searchTerm || (!isBranchRestricted && branchFilter !== 'all') || sortBy !== 'product') && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="text-orange-500 border-orange-500 hover:bg-orange-50"
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search product by name, sku..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 border-gray-300"
                />
              </div>

              {/* Branch Filter */}
              <div>
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  disabled={isBranchRestricted}
                  className={`w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white ${
                    isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="all">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>



              {/* Sort Filter */}
              <div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="product">Sort by Product</option>
                  <option value="branch.name">Sort by Branch</option>
                  <option value="currentStock">Sort by Stock</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-600">
              Showing {filteredData.length} of {inventories.length} inventory items
            </div>
          </CardContent>
        </Card>

        {/* Product Inventory Table */}
        <Card className="bg-white">
          <CardContent className="p-0">
            {filteredData.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your search or filter criteria</p>
                <Button
                  onClick={() => setIsAddModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Inventory
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Stock
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((inventory) => {
                      const currentStock = Number(inventory.currentStock) || 0
                      const threshold = Number(inventory.product?.threshold) || 10
                      const isRecipeBased = inventory.product?.type === 'recipe_based'

                      return (
                        <tr key={inventory._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {safeGet(inventory, 'product.name', 'Unknown Product')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {safeGet(inventory, 'product.category.name', 'Uncategorized')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {safeGet(inventory, 'product.sku', 'N/A')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isRecipeBased ? (
                              <div className="relative group">
                                <div className="text-sm font-medium text-gray-500 cursor-help flex items-center">
                                  N/A
                                  <span className="ml-1 text-xs">🍳</span>
                                </div>
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                  This is a recipe-based product
                                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="text-sm font-medium text-gray-900">
                                  {currentStock.toLocaleString()} <span className="text-blue-600">{safeGet(inventory, 'product.unit.abbreviation', 'units')}</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  Threshold: {threshold} {safeGet(inventory, 'product.unit.abbreviation', 'units')}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Package className="h-4 w-4 mr-2 text-gray-400" />
                              {safeGet(inventory, 'branch.name', 'Unknown Branch')}
                            </div>
                            {/* <div className="text-xs text-gray-500">
                              {safeGet(inventory, 'branch.address', '').split(',')[0] || 'N/A'}
                            </div> */}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedInventory(inventory)
                                  setIsViewModalOpen(true)
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                                title="View Product"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {/* <button
                                onClick={() => {
                                  setSelectedInventory(inventory)
                                  setIsEditModalOpen(true)
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                                title="Edit Inventory"
                              >
                                <Edit className="h-4 w-4" />
                              </button> */}
                              <button
                                onClick={() => {
                                  setSelectedInventory(inventory)
                                  setIsDeleteModalOpen(true)
                                }}
                                className="text-gray-400 hover:text-red-600 p-1 rounded"
                                title="Remove from Inventory"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <AddProductInventoryModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onInventoryCreated={handleInventoryCreated}
      />

      <EditProductInventoryModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        inventory={selectedInventory}
        onInventoryUpdated={handleInventoryUpdated}
      />

      <ViewProductModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        inventory={selectedInventory}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleInventoryDeleted}
        title="Remove Product from Inventory"
        message={`Are you sure you want to remove "${selectedInventory?.product?.name}" from "${selectedInventory?.branch?.name}" inventory? This action cannot be undone.`}
        confirmText="Remove"
        confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      />
    </div>
  )
}

export default BranchProducts